import * as React from 'react'
import { clsx } from 'clsx'
import { EditorContent, useEditor, Editor } from '@tiptap/react'
import type {
  Extensions,
  Editor as CoreEditor,
  JSONContent,
} from '@tiptap/core'
import * as Y from 'yjs'
import * as yProtocolAwareness from 'y-protocols/awareness'
import throttle from 'lodash.throttle'
import type { EditorProps } from '@tiptap/pm/view'
import { jsonToYDocBase64State } from '@basehub/tiptap-api-utils'
import type {
  CollaborationExtensionsOptions,
  CollaborationUser,
} from '../extensions/collaboration'
import { JSONValue, OpsBase, UndoManager } from '~/lib/undo-manager'
import './primitive-tip-tap.css'
import '../extensions/rich-text/extensions/linter/linter.css'
import { useYjsStore } from './yjs-store'
import { RepoUndoOps } from '~/replicache/frontend/undo-redo/manager'

// Global focus restoration store - only one at a time
type FocusState = {
  wasFocused: boolean
  selection: { from: number; to: number }
  scrollTop: number
  focusRestorationId: string
}

let pendingFocusRestore: FocusState | null = null

/* -------------------------------------------------------------------------------------------------
 * PrimitiveTipTapEditor
 * - "Primitive" means PRIMITIVE. Don't leak block-related stuff in here!
 * -----------------------------------------------------------------------------------------------*/

type TipTapEventHandler = (props: { editor: CoreEditor }) => void

export type PrimitiveTipTapEditorProps<FocusOp = OpsBase[number]> = {
  instanceId: string
  clientId: string | undefined
  value: string
  onUpdate: (update: string) => void
  getExtensions: (p: CollaborationExtensionsOptions) => Extensions
  readOnly?: boolean
  className?: string
  onCreate?: TipTapEventHandler
  autoFocus?: boolean
  onAutoFocus?: TipTapEventHandler
  onFocus?: TipTapEventHandler
  editorProps?: EditorProps
  throttleUpdate?: number
  setEditor?: (editor: Editor | null) => void
  alwaysShowPlaceholder?: boolean
  blockAwareness?: {
    blockId: string
    isSelected: boolean
    setSelectedBlockAwareness: (value: string) => void
    remoteAwarenessList: { blockId: string; value: string }[] | undefined
  }
  user?: CollaborationUser | null
  style?: React.CSSProperties
  undo?: {
    focusOp: FocusOp | undefined
    manager:
      | UndoManager<RepoUndoOps>
      | UndoManager<
          {
            op: 'customWindowEvent'
            args: { name: string; detail?: JSONValue }
          }[]
        >
      | null
    // only sessionStorage supported because we need a "sessionId" unique per browser tab
    // for yjs' undomanager to work correctly. anyways, localStorage is not a good choice.
    persist?: 'sessionStorage'
  }
  focusRestorationId?: string
}

export const PrimitiveTipTapEditor = <FocusOp extends OpsBase[number]>({
  value,
  onUpdate,
  onCreate,
  autoFocus,
  onAutoFocus,
  getExtensions,
  onFocus,
  readOnly,
  className,
  editorProps,
  throttleUpdate,
  setEditor,
  alwaysShowPlaceholder,
  blockAwareness,
  user,
  instanceId,
  undo: globalUndo,
  clientId: _clientId,
  focusRestorationId,
  ...rest
}: PrimitiveTipTapEditorProps<FocusOp>) => {
  const yDoc = useYjsStore((s) => s.docs.get(instanceId)?.doc)
  const yUndoManager = useYjsStore((s) => s.docs.get(instanceId)?.undoManager)
  const yFragment = yDoc?.getXmlFragment('content')
  const lastAppliedValueRef = useYjsStore(
    (s) => s.docs.get(instanceId)?.lastAppliedValueRef
  )
  const yAwareness = useYjsStore((s) => s.docs.get(instanceId)?.awareness)

  React.useEffect(() => {
    if (yDoc) return

    const doc = new Y.Doc({ guid: instanceId, gc: false })
    const yFragment = doc.getXmlFragment('content')
    const yUndoManager = new Y.UndoManager(yFragment, {
      captureTimeout: 250,
      trackedOrigins: new Set([instanceId]),
      captureTransaction: (tr) => tr.origin === instanceId,
    })
    const yAwareness = new yProtocolAwareness.Awareness(doc)
    useYjsStore.setState((prev) => {
      const docs = new Map(prev.docs)
      docs.set(instanceId, {
        doc,
        undoManager: yUndoManager,
        lastAppliedValueRef: { current: '' },
        awareness: yAwareness,
      })
      return { docs }
    })
  }, [instanceId, yDoc])

  const valueRef = React.useRef<string>(value)
  valueRef.current = value
  const [hasSynced, setHasSynced] = React.useState(false)

  const focusOpRef = React.useRef<FocusOp | null>(globalUndo?.focusOp ?? null)
  focusOpRef.current = globalUndo?.focusOp ?? null

  const getExtensionsRef = React.useRef(getExtensions)
  getExtensionsRef.current = getExtensions

  const extensions = React.useMemo(() => {
    if (!yDoc || !yFragment || !yAwareness || !yUndoManager) return undefined
    return getExtensionsRef.current({
      yDoc,
      yFragment,
      yAwareness,
      yUndoManager,
      instanceId,
      hasGlobalUndoManager: !!globalUndo?.manager,
    })
  }, [
    yDoc,
    yFragment,
    yAwareness,
    yUndoManager,
    instanceId,
    globalUndo?.manager,
  ])
  const extensionsRef = React.useRef(extensions)
  extensionsRef.current = extensions

  // ────────────────────────────────────────────────────────────────────────────
  // helpers GC / release of structs
  // --------------------------------------------------------------------------
  const releaseStackItem = React.useCallback(
    (item?: any) => {
      if (!item?.deletions) return
      // we need a transaction to be able to modify the structs
      yDoc?.transact((tr) => {
        Y.iterateDeletedStructs(tr, item.deletions, (s) => {
          if (s instanceof Y.Item) {
            s.keep = false
          }
        })
      }, 'release-keep')
    },
    [yDoc]
  )

  const scheduleGc = React.useCallback(() => {
    // let the current micro-task finish and launch GC
    Promise.resolve().then(() => {
      if (!yDoc) return
      yDoc.gc = true
      yDoc.transact(() => {}, 'force-gc')
    })
  }, [yDoc])

  // ────────────────────────────────────────────────────────────────────────────
  // SYNC WITH SERVER
  // --------------------------------------------------------------------------
  React.useEffect(() => {
    // 1) Si no hay doc o extensions, no puedo aplicar nada
    if (!yDoc || !extensions || !lastAppliedValueRef) {
      return
    }

    setHasSynced(true)

    let rawValue: string = value
    if (value.startsWith('!!RESET!!')) {
      resetYDocContent(yDoc)
      rawValue = value.slice('!!RESET!!'.length)
    }

    // 2) Si el value es igual al último que apliqué, no hago nada
    if (rawValue === lastAppliedValueRef.current) {
      return
    }

    // 3) ¡Hay que sincronizar! (o es primera vez, o llegó un update)
    syncYDoc({ value: rawValue, yDoc, extensions, _instanceId: instanceId })

    // 4) Guardar que este value ya fue aplicado
    // eslint-disable-next-line react-compiler/react-compiler
    lastAppliedValueRef.current = rawValue
  }, [yDoc, extensions, value, instanceId, lastAppliedValueRef])

  const throttleOnUpdate = React.useMemo(
    () =>
      throttle(
        (base64: string) => {
          onUpdate(base64)
        },
        throttleUpdate ?? 250,
        { trailing: true, leading: false }
      ),
    [onUpdate, throttleUpdate]
  )

  React.useEffect(() => {
    if (!yDoc) return

    const persistRemote = (_: any, __: any, ___: any, tr: Y.Transaction) => {
      // ignore updates that you already applied via BroadcastChannel
      if (tr.origin?.toString().startsWith('sync:' + instanceId)) return

      const clean = compactSnapshot(yDoc)
      throttleOnUpdate(Buffer.from(clean).toString('base64'))
    }

    yDoc.on('update', persistRemote)
    return () => {
      yDoc.off('update', persistRemote)
    }
  }, [yDoc, instanceId, throttleOnUpdate])

  // ────────────────────────────────────────────────────────────────────────────
  // AWARENESS
  // --------------------------------------------------------------------------
  // Store past IDs to remove awareness states when the user leaves the block
  const pastClientIDs = React.useRef<Set<string>>(new Set())

  // Apply remote user awareness in real time -> cursor position, selection, etc.
  React.useMemo(() => {
    if (!hasSynced || !blockAwareness?.remoteAwarenessList || !yAwareness) {
      return
    }
    const inThisLoop: string[] = []
    for (const remote of blockAwareness.remoteAwarenessList) {
      const [remoteClientId, remoteValue] = remote.value.split(':')

      if (
        typeof remoteClientId === 'string' &&
        typeof remoteValue === 'string'
      ) {
        inThisLoop.push(remoteClientId)
        yProtocolAwareness.applyAwarenessUpdate(
          yAwareness,
          // @ts-ignore
          Buffer.from(remoteValue, 'base64'),
          'origin'
        )
      }
    }

    for (const clientId of pastClientIDs.current) {
      if (!inThisLoop.includes(clientId)) {
        yProtocolAwareness.removeAwarenessStates(
          yAwareness,
          [Number(clientId)],
          'origin'
        )
        pastClientIDs.current.delete(clientId)
      }
    }

    for (const clientId of inThisLoop) {
      pastClientIDs.current.add(clientId)
    }
  }, [yAwareness, hasSynced, blockAwareness?.remoteAwarenessList])

  React.useEffect(() => {
    if (!blockAwareness || !yAwareness) return
    const f = async ({
      added,
      updated,
      removed,
    }: {
      added: number[]
      updated: number[]
      removed: number[]
    }) => {
      if (
        added.includes(yAwareness.clientID) ||
        updated.includes(yAwareness.clientID) ||
        removed.includes(yAwareness.clientID)
      ) {
        if (blockAwareness?.isSelected) {
          const awarenessUpdate = yProtocolAwareness.encodeAwarenessUpdate(
            yAwareness,
            [yAwareness.clientID]
          )

          blockAwareness.setSelectedBlockAwareness(
            yAwareness.clientID +
              ':' +
              Buffer.from(awarenessUpdate).toString('base64')
          )
        }
      }
    }
    yAwareness.on('change', f)
    return () => yAwareness.off('change', f)
  }, [
    yAwareness,
    blockAwareness,
    blockAwareness?.blockId,
    blockAwareness?.isSelected,
    blockAwareness?.setSelectedBlockAwareness,
  ])

  React.useEffect(() => {
    if (!user || !yAwareness) return
    yAwareness.setLocalStateField('user', user)
  }, [yAwareness, user])

  // ────────────────────────────────────────────────────────────────────────────
  // LOCAL SYNC
  // --------------------------------------------------------------------------
  React.useEffect(() => {
    if (!yDoc || !yUndoManager) return
    const { docs } = useYjsStore.getState()
    const isFresh = !docs.has(instanceId) // or simply docs.get check

    const channel = new BroadcastChannel('yjs-' + instanceId)

    if (isFresh) {
      channel.postMessage({ type: 'REQUEST_SYNC' })
    }

    const onMessage = (ev: MessageEvent) => {
      const msg = ev.data
      if (!msg?.type) return

      switch (msg.type) {
        case 'REQUEST_SYNC':
          {
            const full = Y.encodeStateAsUpdate(yDoc)
            channel.postMessage({ type: 'FULL_SYNC', update: full })
          }
          break

        case 'FULL_SYNC':
          Y.applyUpdateV2(yDoc, msg.update, 'local-sync:' + instanceId)
          break

        case 'DELTA_SYNC':
          Y.applyUpdateV2(yDoc, msg.update, 'local-sync:' + instanceId)
          break
      }
    }
    channel.addEventListener('message', onMessage)

    // ➌ Every time this tab changes, send only the delta:
    const localSync = (update: Uint8Array, origin: any) => {
      if (origin === 'local-sync:' + instanceId) return
      channel.postMessage({ type: 'DELTA_SYNC', update })
    }
    yDoc.on('update', localSync)

    return () => {
      yDoc.off('update', localSync)
      channel.removeEventListener('message', onMessage)
      channel.close()
    }
  }, [yDoc, instanceId, yUndoManager])

  // ────────────────────────────────────────────────────────────────────────────
  //  GLOBAL UNDO MANAGER
  // --------------------------------------------------------------------------
  React.useEffect(() => {
    if (!globalUndo?.manager || !yUndoManager || !hasSynced) return

    // TRIM inside the callback and not outside
    const LIMIT = 200
    const stackItemAdded = ({ origin }: { origin: any }) => {
      if (origin !== instanceId) return

      globalUndo.manager?.push({
        undo: [
          ...(focusOpRef.current ? [focusOpRef.current as any] : []),
          { op: 'yjs', args: { id: instanceId, dir: 'undo' } },
        ],
        redo: [
          ...(focusOpRef.current ? [focusOpRef.current as any] : []),
          { op: 'yjs', args: { id: instanceId, dir: 'redo' } },
        ],
      })
      // ── TRIM + release structs ────────────────────────────────────────────
      if (yUndoManager.undoStack.length > LIMIT) {
        const removed = yUndoManager.undoStack.splice(
          0,
          yUndoManager.undoStack.length - LIMIT
        )
        removed.forEach(releaseStackItem)
        scheduleGc()
      }
      if (yUndoManager.redoStack.length > LIMIT) {
        const removed = yUndoManager.redoStack.splice(
          0,
          yUndoManager.redoStack.length - LIMIT
        )
        removed.forEach(releaseStackItem)
        scheduleGc()
      }
    }

    const stackItemPopped = ({ stackItem }: { stackItem: any }) => {
      if (stackItem) {
        releaseStackItem(stackItem)
        scheduleGc()
      }
    }

    yUndoManager.on('stack-item-added', stackItemAdded)
    yUndoManager.on('stack-item-popped', stackItemPopped)
    return () => {
      yUndoManager.off('stack-item-added', stackItemAdded)
      yUndoManager.off('stack-item-popped', stackItemPopped)
    }
  }, [
    yUndoManager,
    globalUndo?.manager,
    globalUndo?.persist,
    instanceId,
    hasSynced,
    yDoc,
    releaseStackItem,
    scheduleGc,
  ])

  // ────────────────────────────────────────────────────────────────────────────
  // FLUSH THROTTLE WHEN UNMOUNT OR SWITCH
  // --------------------------------------------------------------------------

  if (!yDoc || !extensions || !hasSynced) {
    return <div {...rest} className={className} />
  }
  return (
    <PrimitiveTipTapEditorInternal
      className={className}
      extensions={extensions}
      yDoc={yDoc}
      setEditor={setEditor}
      alwaysShowPlaceholder={alwaysShowPlaceholder}
      autoFocus={autoFocus}
      onAutoFocus={onAutoFocus}
      onFocus={onFocus}
      readOnly={readOnly}
      editorProps={editorProps}
      onCreate={onCreate}
      focusRestorationId={focusRestorationId}
      {...rest}
    />
  )
}

const PrimitiveTipTapEditorInternal = ({
  extensions,
  editorProps,
  onCreate,
  onFocus,
  readOnly,
  className,
  alwaysShowPlaceholder,
  yDoc,
  setEditor,
  autoFocus,
  onAutoFocus,
  focusRestorationId,
  ...rest
}: {
  extensions: Extensions
  editorProps?: EditorProps
  onCreate?: TipTapEventHandler
  onFocus?: TipTapEventHandler
  readOnly?: boolean
  className?: string
  yDoc: Y.Doc
  alwaysShowPlaceholder?: boolean
  setEditor: PrimitiveTipTapEditorProps['setEditor']
  autoFocus?: boolean
  onAutoFocus?: TipTapEventHandler
  focusRestorationId?: string
}) => {
  // Track focus state reliably with refs
  const focusStateRef = React.useRef<{
    wasFocused: boolean
    selection: { from: number; to: number }
    scrollTop: number
  }>({
    wasFocused: false,
    selection: { from: 0, to: 0 },
    scrollTop: 0,
  })

  const editor = useEditor(
    {
      extensions,
      editorProps,
      ...(onCreate ? { onCreate } : {}),
      ...(onFocus ? { onFocus } : {}),
      editable: !readOnly,
      immediatelyRender: false,
    },
    [extensions]
  )

  // Track focus state continuously using editor events
  React.useEffect(() => {
    if (!editor) return

    let blurTimeout: NodeJS.Timeout | null = null

    const updateFocusState = () => {
      focusStateRef.current = {
        wasFocused: editor.isFocused,
        selection: {
          from: editor.state.selection.from,
          to: editor.state.selection.to,
        },
        scrollTop: editor.view.dom.scrollTop,
      }
    }

    const onFocus = () => {
      // Clear any pending blur timeout
      if (blurTimeout) {
        clearTimeout(blurTimeout)
        blurTimeout = null
      }

      focusStateRef.current.wasFocused = true
      updateFocusState()
      console.log('Editor focused, updated state:', focusStateRef.current)
    }

    const onBlur = () => {
      // Debounce blur to avoid marking as unfocused during component recreation
      blurTimeout = setTimeout(() => {
        focusStateRef.current.wasFocused = false
        console.log('Editor blurred (debounced), state:', focusStateRef.current)
        blurTimeout = null
      }, 100) // Short delay to handle component recreation
    }

    const onSelectionUpdate = () => {
      if (editor.isFocused) {
        updateFocusState()
      }
    }

    // Listen to editor events
    editor.on('focus', onFocus)
    editor.on('blur', onBlur)
    editor.on('selectionUpdate', onSelectionUpdate)

    // Initial state
    updateFocusState()

    return () => {
      editor.off('focus', onFocus)
      editor.off('blur', onBlur)
      editor.off('selectionUpdate', onSelectionUpdate)

      // Clear timeout on cleanup
      if (blurTimeout) {
        clearTimeout(blurTimeout)
      }
    }
  }, [editor])

  // Store focus state globally when component unmounts
  React.useEffect(() => {
    return () => {
      if (focusRestorationId) {
        const currentState = focusStateRef.current
        console.log('Storing focus state on unmount:', {
          focusRestorationId,
          ...currentState,
        })
        pendingFocusRestore = {
          ...currentState,
          focusRestorationId,
        }
      }
    }
  }, [focusRestorationId])

  // Restore focus state from global store on component mount
  React.useEffect(() => {
    if (!editor || !focusRestorationId) return

    // Check if this is the correct restoration target
    if (
      !pendingFocusRestore ||
      pendingFocusRestore.focusRestorationId !== focusRestorationId
    ) {
      return
    }

    const storedFocusState = pendingFocusRestore
    if (!storedFocusState.wasFocused) {
      // Clear the pending state even if not restoring
      pendingFocusRestore = null
      return
    }

    console.log('Restoring focus state from global store', {
      focusRestorationId,
      selection: storedFocusState.selection,
      scrollTop: storedFocusState.scrollTop,
    })

    // Use requestAnimationFrame to ensure DOM is ready
    requestAnimationFrame(() => {
      // Double-check editor is still valid
      if (!editor || !editor.view || editor.isDestroyed) {
        pendingFocusRestore = null
        return
      }

      try {
        // Restore scroll position
        if (editor.view.dom) {
          editor.view.dom.scrollTop = storedFocusState.scrollTop
        }

        // Focus the editor
        editor.commands.focus()

        // Try to restore the exact selection
        const maxPos = editor.state.doc.content.size
        const from = Math.min(storedFocusState.selection.from, maxPos)
        const to = Math.min(storedFocusState.selection.to, maxPos)

        if (from !== to || from > 0) {
          editor.commands.setTextSelection({ from, to })
        }

        console.log('Focus restoration completed successfully')
      } catch (e) {
        // Fallback: try to focus at the end
        console.warn(
          'Could not restore exact selection, trying fallback focus:',
          e
        )
        try {
          editor.commands.focus('end')
        } catch (fallbackError) {
          console.warn('Fallback focus also failed:', fallbackError)
        }
      } finally {
        // Always clean up the stored state
        pendingFocusRestore = null
      }
    })
  }, [editor, focusRestorationId])

  // sync with parent state
  const setEditorRef = React.useRef(setEditor)
  // eslint-disable-next-line react-compiler/react-compiler
  setEditorRef.current = setEditor
  React.useEffect(() => {
    setEditorRef.current?.(editor)
  }, [editor])

  // handle autoFocus
  React.useEffect(() => {
    if (!autoFocus) return
    if (!editor) return
    if (editor.isFocused) return
    const success = editor.commands.focus()
    if (success) onAutoFocus?.({ editor })
  }, [autoFocus, editor, onAutoFocus])

  // handles special placeholder for visitors
  React.useEffect(() => {
    if (!editor) return
    if (editor.isEmpty && readOnly) {
      editor.view.dom.classList.add('visitor-placeholder')
    } else {
      editor.view.dom.classList.remove('visitor-placeholder')
    }
  }, [editor, editor?.isEmpty, readOnly])

  const ydocIsReady = yDoc && editor && editor.getText() !== undefined

  if (!ydocIsReady) return <div {...rest} className={className} />

  return (
    <EditorContent
      {...rest}
      className={clsx(
        className,
        'primitive-tiptap-editor',
        alwaysShowPlaceholder && 'always-show-placeholder'
      )}
      editor={editor}
      readOnly={readOnly}
      onContextMenu={(e) => e.stopPropagation()}
    />
  )
}

export function syncYDoc({
  value,
  yDoc,
  extensions,
  _instanceId,
}: {
  value: string
  yDoc: Y.Doc
  extensions: Extensions
  _instanceId: string
}) {
  const SYNC_ORIGIN = 'sync:' + _instanceId
  let b64: string

  try {
    // if it comes as JSON, we convert it to base64 v2
    b64 = jsonToYDocBase64State(
      JSON.parse(value) as JSONContent,
      extensions
    ).base64
  } catch {
    // if it was already base64, we take it directly
    b64 = value
  }
  if (!b64) return
  const u8 = Buffer.from(b64, 'base64')

  // 1) Try v2 directly
  try {
    Y.applyUpdateV2(yDoc, u8, SYNC_ORIGIN)
    return
  } catch {
    // 2) If it failed, assume it's v1 package: apply it ONCE and that's it
    Y.applyUpdate(yDoc, u8, SYNC_ORIGIN)
    // NOTE: we don't apply v2 again here. The doc already contains the state,
    // Yjs will internally migrate everything to v2 structures on the next encode.
    return
  }
}

function compactSnapshot(src: Y.Doc): Uint8Array {
  // clone: prevents any struct from remaining marked as keep
  const tmp = new Y.Doc({ gc: true })
  Y.applyUpdateV2(tmp, Y.encodeStateAsUpdateV2(src))
  // second GC pass just in case
  tmp.gc = true
  tmp.transact(() => {}, 'force-gc/clone')

  return Y.encodeStateAsUpdateV2(tmp)
}

function resetYDocContent(yDoc: Y.Doc, key = 'content') {
  yDoc.transact(() => {
    const frag = yDoc.getXmlFragment(key)
    while (frag.length) {
      frag.delete(0, frag.length)
    }
  }, 'reset-before-sync')
}
